import { baseApi } from "@/shared/api";
import type { SigningActionResponse } from "@/shared/types";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type {
  PremisesHandoverResponse,
  PremisesHandoverAct,
  PremisesHandoverListResponse,
  PremisesHandoverApiPayload,
} from "../types";

export const premisesHandoverApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPremisesHandover: builder.query<PremisesHandoverResponse, number>({
      query: (id) => ({
        url: `/api/legal/premises-handovers/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "PremisesHandovers" as const, id }],
    }),

    getPremisesHandoverList: builder.query<PremisesHandoverAct[], void>({
      query: () => ({
        url: `/api/legal/premises-handovers/`,
        method: "GET",
      }),
      transformResponse: (response: PremisesHandoverListResponse): PremisesHandoverAct[] => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "PremisesHandovers" as const, id })),
              { type: "PremisesHandovers" as const, id: "LIST" },
            ]
          : [{ type: "PremisesHandovers" as const, id: "LIST" }],
    }),

    submitPremisesHandoverForSigning: builder.mutation<SigningActionResponse, { id: number; cms_signature: string; file_base64: string }>({
      query: ({ id, cms_signature, file_base64 }) => ({
        url: `/api/legal/premises-handovers/${id}/submit-for-signing/`,
        method: "POST",
        body: { cms_signature, file_base64 },
        credentials: "include" as const,
      }),
      invalidatesTags: () => [
        { type: "PremisesHandovers" as const, id: "LIST" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          premisesHandoverApi.util.updateQueryData("getPremisesHandover", id, (draft) => {
            draft.signing_status = 1;
          }),
        );
      },
    }),

    downloadPremisesHandoverPreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/legal/premises-handovers/${id}/preview/`,
        method: "GET",
        responseHandler: fileResponseHandler,
      }),
    }),

    createPremisesHandover: builder.mutation<PremisesHandoverResponse, PremisesHandoverApiPayload>({
      query: (body) => ({
        url: "/api/legal/premises-handovers/",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "PremisesHandovers" as const, id: "LIST" },
        { type: "PremisesLeases" as const, id: "LIST" },
      ],
    }),

    deletePremisesHandover: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/premises-handovers/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PremisesHandovers" as const, id },
        { type: "PremisesHandovers" as const, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetPremisesHandoverQuery,
  useGetPremisesHandoverListQuery,
  useSubmitPremisesHandoverForSigningMutation,
  useDownloadPremisesHandoverPreviewMutation,
  useCreatePremisesHandoverMutation,
  useDeletePremisesHandoverMutation,
} = premisesHandoverApi;

